import { MathUtils, Mesh, PlaneBufferGeometry, TextureLoader, Vector3 } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import AppEngine from './components/general/appEngine.js';
import Globe from './components/globe/Globe';
import Asset3DLibrary from './utils/asset3dLibrary';
import SatelliteController from './components/globe/SatelliteController';
import GlobeState from './components/globe/GlobeState';
import Reticle from './components/ui/Reticle';
import BackgroundMaterial from './components/general/BackgroundMaterial';
import LatLong from './components/general/LatLong';
import PinGroupManager from './components/globe/PinGroupManager';

export default class WebGLApp {
  constructor() {
    this.checkOnLand = false;
    this.onLabelsRequested = null;
  }

  async init(_canvas, _canvasWidth, _canvasHeight, _baseUrl) {
    // show detailed pins once we're in this zoom level. Between 0 and 1. Higher levels need more zooming.
    this.detailZoomThreshold = 0.8;
    // The amount of smoothing to apply when the camera points away from the globe centre (
    this.pivotSmoothing = 0.95;

    this.appEngine = new AppEngine(_canvas, _canvasWidth, _canvasHeight);
    // Mali GPUs report Floating Point texture support in some browsers, breaking Three's animations. If they do this,
    // disable them entirely.
    this.vertexAnimationsBroken =
      this.appEngine.info.renderer.indexOf('Mali') >= 0 &&
      this.appEngine.renderer.extensions.get('OES_texture_float');

    this.onResize = (...args) => this.appEngine.onResize.call(this.appEngine, ...args);

    // Waldorf
    this.userLat = 49.291515;
    this.userLong = 8.646999;

    this.onUserRotation = null;
    this.onPinClicked = null;
    this.onUserPinClicked = null;

    // states we can select
    this.introState = new GlobeState('intro', 0.0, 3.5, 0.95);
    this.introState.zoomCurve = 2.0; // curving the zoom makes the pins appear sooner
    this.introState.pinPhase = 0;
    this.selectLocationState = new GlobeState('selectLocation', 0.7, 0.0, 0.8);
    this.selectLocationState.lat = this.userLat;
    this.selectLocationState.long = this.userLong;
    this.globalShockState = new GlobeState('globalShock', 0.4, 8.0, 0.95);
    this.globalShockState.lat = 20.0;
    this.questionaireState = new GlobeState('questionnaire', 1.0, 7.0, 0.95);
    this.questionaireState.pivot = new Vector3(0.5, -0.7, 0.3);
    this.afterState = new GlobeState('after', 1.0, 7.0, 0.95);
    this.afterState.pivot = new Vector3(0.5, -0.7, 0.3);

    this.beforeResultState = new GlobeState('beforeResult', 1, 0.0, 0.01);
    this.beforeResultState.labelSize = 0.0;

    this.resultState = new GlobeState('result', 0.8, 0.0, 0.95);
    this.resultState.labelSize = 0.0;
    this.resultState.pivot = new Vector3(0, 0, 0);

    this.browseCompanyState = new GlobeState('result', 0.8, 0.0, 0.95);
    this.browseCompanyState.labelSize = 1.0;

    this.introState.activate = () => {
      this.reticle.pinColor = 0xffffff;
      // on restart, remove the label
      this.reticle.removeLabel();
      // when reset before finishing
      this.globe.removeLastingShockEffect();
      this.hideReticle();
    };

    this.selectLocationState.activate = () => {
      this.controller.startInteraction();
      this.checkOnLand = true;
      // upon restarting or so?
      this.globe.hideGlobalShock();
      this.showReticle();
    };

    this.selectLocationState.deactivate = () => {
      this.controller.stopInteraction();
      this.checkOnLand = false;
      this.userLat = this.controller.latitude;
      this.userLong = this.controller.longitude;
      this.beforeResultState.lat = this.userLat - 5;
      this.beforeResultState.long = this.userLong - 2.5;
      this.resultState.lat = this.userLat - 12;
      this.resultState.long = this.userLong - 4;
      this.attachReticleToGlobe();
    };

    // this.globalShockState.activate = () => this.globe.showGlobalShock();

    this.afterState.activate = () => this.globe.removeLastingShockEffect();

    this.browseCompanyState.activate = () => {
      this.controller.startInteraction();
      this.pinGroupManager.startInteraction();
      // this.globe.removeLastingShockEffect();
      //  this.reticle.playAnimation();
    };
    this.browseCompanyState.deactivate = () => {
      this.controller.stopInteraction();
      this.pinGroupManager.stopInteraction();
    };

    this.currentState = null;
    this.transitionValue = 0.0;
    this.targetPivot = new Vector3();
    this.transitionInitData = new GlobeState('init');

    this.globeRadius = 1;

    this.assetLibrary = new Asset3DLibrary(_baseUrl);
    this.assetLibrary.queueAsset('skybox', 'textures/skybox.jpg', TextureLoader);
    this.assetLibrary.queueAsset('irradiance', 'textures/skybox-irradiance.jpg', TextureLoader);
    // using a custom SDF texture instead of a normal albedo map so edges will look crisp even when zoomed in
    this.assetLibrary.queueAsset('globe/sdf', 'textures/globe/earth-sdf.jpg', TextureLoader);
    this.assetLibrary.queueAsset('pin/model', 'models/pin_from_blender.gltf', GLTFLoader);
    this.assetLibrary.queueAsset('pin/user', 'models/user_pin.gltf', GLTFLoader);
    this.assetLibrary.queueAsset('bg/gradient', 'textures/bg-gradient-2.png', TextureLoader);
    this.assetLibrary.queueAsset('pin/ripple', 'textures/ripple.jpg', TextureLoader);
    this.assetLibrary.queueAsset('globe/noise', 'textures/noise.jpg', TextureLoader);
    this.assetLibrary.queueAsset(
      'globe/normalDetailMap',
      'textures/globe/globe-detail-normals.jpg',
      TextureLoader
    );

    this.assetLibrary.queueAsset(
      'globe/lightsPattern',
      'textures/globe/earth-lights-pattern.jpg',
      TextureLoader
    );

    this.assetLibrary.queueAsset(
      'globe/emission',
      'textures/globe/earth-emission.jpg',
      TextureLoader
    );

    this.assetLibrary.queueAsset(
      'globe/normals',
      'textures/globe/earth-normals.jpg',
      TextureLoader
    );

    return new Promise(resolve => {
      // this.assetLibrary.addEventListener('progress', this.onAssetsProgress);
      this.assetLibrary.addEventListener('error', error => {
        // eslint-disable-next-line no-console
        console.error(error);
      });
      this.assetLibrary.addEventListener('complete', () => {
        this.onAssetsLoaded();
        resolve();
      });
      this.assetLibrary.load();
    });
  }

  onAssetsLoaded = () => {
    this.globe = new Globe(this.appEngine.renderer, this.assetLibrary, this.globeRadius);
    this.appEngine.addToScene(this.globe);

    this.background = new Mesh(
      new PlaneBufferGeometry(),
      new BackgroundMaterial(this.assetLibrary.get('bg/gradient'))
    );
    this.appEngine.addToScene(this.background);

    const sdf = this.assetLibrary.get('globe/sdf').image;
    this._waterCanvas = document.createElement('canvas');
    this._waterCanvas.width = sdf.naturalWidth;
    this._waterCanvas.height = sdf.naturalHeight;
    this._waterCanvas.getContext('2d').drawImage(sdf, 0, 0, sdf.naturalWidth, sdf.naturalHeight);

    this.controller = new SatelliteController(
      this.appEngine.camera,
      this.globe,
      document.getElementById('app')
    );
    this.controller.onUserRotation = this._onUserRotation; // this will only trigger when interaction is enabled

    // set the intro to focus "somewhat" on the user's localhost:8080/#/(somewhat aesthetically pleasing)
    this.introState.lat = this.userLat * 0.75;
    this.introState.long = this.userLong - 10;

    this.reticle = new Reticle(this.assetLibrary, this.globeRadius, !this.vertexAnimationsBroken);

    this.pinGroupManager = new PinGroupManager(this.appEngine.camera, this.globe, this.reticle);
    this.pinGroupManager.onPinClicked = pin => {
      this.centerPin(pin);
      if (this.onPinClicked) this.onPinClicked(pin);
    };
    this.pinGroupManager.onUserPinClicked = () => {
      if (this.onUserPinClicked) this.onUserPinClicked();
    };

    // need at least some intro state, even if it's overwritten
    this.setState(this.introState, false);

    this.animate();
  };

  animate = () => {
    // prevent large time-steps from alt-tabbing
    const dt = Math.min(this.appEngine.clock.getDelta(), 1 / 30);
    requestAnimationFrame(this.animate);
    // if (this.debugGlobeInteraction) this.debugGlobeInteraction.update(dt);
    if (!this.appEngine.renderer.isEnabled) return;
    this.globe.position.lerp(this.targetPivot, 1.0 - this.pivotSmoothing);
    this.controller.update(dt);
    const showDetailedPins = this.controller.zoomFactor > this.detailZoomThreshold;

    this.pinGroupManager.updateShownPins(
      showDetailedPins,
      this.controller.latitude,
      this.controller.longitude,
      this.onLabelsRequested
    );
    this.globe.update(dt);
    let movement = 0.0;
    if (this.currentState === this.selectLocationState) {
      movement = this.controller.rotationAmount;
    }

    this.reticle.update(dt, movement);
    const bgUniforms = this.background.material.uniforms;
    const offs1 = bgUniforms.offset1.value;
    const offs2 = bgUniforms.offset2.value;
    offs1.x += 0.03 * dt;
    offs1.y += 0.06 * dt;
    offs2.x -= 0.04 * dt;
    offs2.y -= 0.03 * dt;
    this.appEngine.render();
  };

  setStateById(stateId, ...props) {
    const state = this[stateId];
    this.setState(state, ...props);
  }

  /**
   * Sets a state immediately. Smooth defines whether or not the globe should snap to it's target immediately.
   */
  setState(state, smooth = true) {
    if (this.currentState === state) return;

    this.clearState();
    if (state) {
      this.controller.zoomFactor = state.zoomFactor;
      if (state.lat !== undefined) this.controller.latitude = state.lat;
      if (state.long !== undefined) this.controller.longitude = state.long;
      this.controller.autoRotationSpeed = state.rotationSpeed;
      this.controller.zoomFactorSmoothing = state.zoomFactorSmoothing;
      this.globe.pinPhase = state.pinPhase;
      this.globe.labelSize = state.labelSize;
      this.targetPivot.copy(state.pivot);

      if (!smooth) {
        this.controller.snapToPosition();
        this.globe.position.copy(state.pivot);
      }

      this.transitionValue = 0;
      this.currentState = state;
      this.currentState.activate();

      if (state !== this.introState && state !== this.selectLocationState) {
        // these are the only states where the pin is attached
        this.attachReticleToGlobe();
      }
    }
  }

  clearState() {
    if (this.currentState) {
      this.currentState.deactivate();
      this.currentState = null;
    }
  }

  lerp(a, b, t) {
    const len = a.length;
    if (b.length != len) return;

    const x = [];
    for (let i = 0; i < len; i++) x.push(a[i] + t * (b[i] - a[i]));
    return x;
  }

  /**
   * Transitions from the current state to the target state. Value is between 0 - 1. When it's one, the target
   * state will be made current.
   */
  transitionBetweenById(currentStateId, targetStateId, ...props) {
    const currentState = this[currentStateId];
    const targetState = this[targetStateId];

    this.transitionBetween(currentState, targetState, ...(props || {}));

    if (currentStateId == 'selectLocationState' && targetStateId == 'globalShockState') {
      this.globe.showGlobalShock();
    }
  }

  /**
   * Transitions from the current state to the target state. Value is between 0 - 1. When it's one, the target
   * state will be made current.
   */
  transitionBetween(currentState, targetState, value) {
    // first time transitioning, need to store the current globe values
    value = MathUtils.clamp(value, 0, 1);

    if (this.transitionValue === 0) {
      this._getTransitionData();
    }

    this.transitionValue = value;

    // intro state is the only state we need to activate when reaching 0, because it's the entry state
    if (value === 0.0 && currentState === this.introState) {
      this.setState(currentState);
    }
    if (value === 1.0) {
      // activate target state
      this.setState(targetState);
    } else {
      // deactivate any active state
      this.clearState();

      const smoothValue = MathUtils.smoothstep(value, 0, 1);

      const currentState = this.transitionInitData;
      this.controller.zoomFactor = MathUtils.lerp(
        currentState.zoomFactor,
        targetState.zoomFactor,
        Math.pow(smoothValue, currentState.zoomCurve)
      );
      this.globe.pinPhase = MathUtils.lerp(currentState.pinPhase, targetState.pinPhase, value);

      if (targetState.lat !== undefined)
        this.controller.latitude = MathUtils.lerp(currentState.lat, targetState.lat, smoothValue);
      if (targetState.long !== undefined) {
        while (currentState.long - targetState.long > 180) currentState.long -= 180;
        while (currentState.long - targetState.long < -180) currentState.long += 180;
        this.controller.longitude = MathUtils.lerp(
          currentState.long,
          targetState.long,
          smoothValue
        );
      }

      this.controller.autoRotationSpeed = MathUtils.lerp(
        currentState.rotationSpeed,
        targetState.rotationSpeed,
        value
      );
      this.controller.zoomFactorSmoothing = MathUtils.lerp(
        currentState.zoomFactorSmoothing,
        targetState.zoomFactorSmoothing,
        value
      );
      this.targetPivot.copy(currentState.pivot).lerp(targetState.pivot, smoothValue);
      this.globe.labelSize = MathUtils.lerp(currentState.labelSize, targetState.labelSize, value);

      // Play pin animation only on restult state
      if (targetState.name === 'result') {
        // TODO: move some of the parameters to the reticle class
        const highScale = [0.03, 0.03, 0.03];
        const lowScale = [0.01, 0.0065, 0.01];

        const scale = this.lerp(highScale, lowScale, value);
        this.reticle.setScale(scale);
        // console.log('user_pin_scale ' + scale);
        if (value >= 0.035) this.reticle.playAnimation();
      }
      if (targetState.name === 'selectLocation') {
        this.reticle.resetAnimation();
        const scale = [0.025, 0.025, 0.025];
        this.reticle.setScale(scale);
      }
    }
  }

  /**
   * Animate the globe to focus on the user's lat/long coordinates and start user interaction.
   */
  startInteraction() {
    // this.controller.movementSmoothing = 0.9;
    // this.controller.zoomFactorSmoothing = 0.9;
    this.controller.latitude = this.userLat;
    this.controller.longitude = this.userLong;
    this.controller.zoomFactor = 0.3;
    this.controller.startInteraction();
    this.controller.autoRotationSpeed = 0;
  }

  /**
   * Ends the interactive state and keeps the globe where it is.
   */
  stopInteraction() {
    this.controller.stopInteraction();
  }

  /**
   * A value from 0 to 1 to indicate the min/max zoom level. Changing values will animate.
   */
  get zoomFactor() {
    return this.controller.zoomFactor;
  }

  set zoomFactor(value) {
    this.controller.zoomFactor = value;
  }

  /**
   * @param arr For now, an array containing { lat, long, value } objects. TODO: Will need to solidify with real data.
   */
  setPins(groupPins) {
    this.pinGroupManager.groupPins = groupPins;
    this.globe.setGroupPins(groupPins);
    this.globe.setDetailPins(null);
  }

  _isOnLand(location) {
    // the signed distance to the land / water boundary
    let u = (location.long + 180) / 360;
    let v = (-location.lat + 90) / 180;

    // "fract" part of uvs (causes wrapping)
    u -= Math.floor(u);
    v -= Math.floor(v);

    u *= this._waterCanvas.width;
    v *= this._waterCanvas.height;

    const pixelData = this._waterCanvas.getContext('2d').getImageData(u, v, 1, 1).data;

    return pixelData[0] > 0x80;
  }

  /**
   * This is used at the start of a transition so we know where we're transitioning *from*
   * @private
   */
  _getTransitionData() {
    this.transitionInitData.lat = this.controller.latitude;
    this.transitionInitData.long = this.controller.longitude;
    this.transitionInitData.zoomFactor = this.controller.zoomFactor;
    this.transitionInitData.zoomFactorSmoothing = this.controller.zoomFactorSmoothing;
    this.transitionInitData.rotationSpeed = this.controller.autoRotationSpeed;
    this.transitionInitData.pivot.copy(this.globe.position);
    this.transitionInitData.pinPhase = this.globe.pinPhase;
    if (this.currentState) {
      this.transitionInitData.zoomCurve = this.currentState.zoomCurve;
      this.transitionInitData.labelSize = this.currentState.labelSize;
    }
  }

  showReticle() {
    // make sure the shown ripple starts with a pulse
    this.reticle.phase = 0;
    this.appEngine.scene.add(this.reticle);
    this.reticle.viewAlign();
  }

  hideReticle() {
    if (this.reticle.parent) this.reticle.parent.remove(this.reticle);
  }

  attachReticleToGlobe() {
    // TODO: Probably just try and use user lat/long
    // let quat = this.globe.quaternion.clone().inverse();
    // this.reticle.position.applyQuaternion(quat);
    this.globe.add(this.reticle);
    LatLong.toVector3(this.userLat, this.userLong, this.globeRadius, this.reticle.position);
    this.reticle.lookAt(this.reticle.parent.position);
    this.reticle.updateMatrixWorld(true);
  }

  // accepting pins because they may be filtered from the main pins
  setLabels(canvases, pins) {
    // console.log('*setLabels called*');
    this.globe.setLabels(canvases, pins);
  }

  setUserLabel(canvas) {
    this.reticle.setLabel(canvas);
  }

  removeUserLabel() {
    this.reticle.removeLabel();
  }

  /**
   * Emits a { lat, long } object containing the user's selected location.
   */
  _onUserRotation = (lat, long) => {
    if (!this.onUserRotation) return;

    const location = { lat, long };

    while (long >= 180) long -= 360;
    while (long < -180) long += 360;

    const isOnLand = this.checkOnLand ? this._isOnLand(location) : true;

    this.reticle.isValid = isOnLand;
    this.onUserRotation(isOnLand ? location : null);
  };

  centerPin(pin) {
    this.controller.movementSmoothing = 0.88;
    this.controller.latitude = pin.location.lat - 2;
    this.controller.longitude = pin.location.long - 2;
  }
}
